import React, { useState, useEffect, useRef } from 'react';
import { jobAssignmentsAPI, jobsAPI, teamMembersAPI, leadsAPI } from '../utils/api';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, parseISO, isWithinInterval } from 'date-fns';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Printer, ClipboardList, Target } from 'lucide-react';

const EstimatorCalendar = () => {
  const [jobAssignments, setJobAssignments] = useState([]);
  const [leadAssignments, setLeadAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    loadData();
  }, [currentWeekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, jobsRes, membersRes, leadsRes] = await Promise.all([
        jobAssignmentsAPI.getAll(),
        jobsAPI.getAll(),
        teamMembersAPI.getAll(),
        leadsAPI.getAll()
      ]);
      
      // Filter team members who are estimators
      const estimatorMembers = membersRes.data.filter(m => 
        m.role === 'estimator' || 
        m.job_description?.toLowerCase().includes('estimator') ||
        m.skills?.some(s => s.toLowerCase().includes('estimat'))
      );
      setEstimators(estimatorMembers);
      
      // Get estimator IDs for filtering assignments
      const estimatorIds = new Set(estimatorMembers.map(e => e.id));
      
      // Filter job assignments to only include those for estimators
      const estimatorJobAssignments = assignmentsRes.data.filter(a => estimatorIds.has(a.member_id));
      setJobAssignments(estimatorJobAssignments);
      setJobs(jobsRes.data);
      setLeads(leadsRes.data);
      
      // Extract lead assignments from leads (filter for estimators)
      const allLeadAssignments = [];
      leadsRes.data.forEach(lead => {
        if (lead.assignments && lead.assignments.length > 0) {
          lead.assignments.forEach(a => {
            if (estimatorIds.has(a.member_id)) {
              allLeadAssignments.push({
                ...a,
                lead_id: lead.id,
                lead_code: lead.lead_code,
                lead_title: lead.title,
                is_lead: true
              });
            }
          });
        }
      });
      setLeadAssignments(allLeadAssignments);
    } catch (error) {
      toast.error('Failed to load estimator calendar data');
    } finally {
      setLoading(false);
    }
  };

  const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });

  // Get job assignments that overlap with a specific day
  const getJobAssignmentsForDay = (day) => {
    return jobAssignments.filter(assignment => {
      if (assignment.status === 'cancelled') return false;
      const start = parseISO(assignment.assignment_start);
      const end = parseISO(assignment.assignment_end);
      return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end);
    });
  };

  // Get lead assignments that overlap with a specific day
  const getLeadAssignmentsForDay = (day) => {
    return leadAssignments.filter(assignment => {
      if (assignment.status === 'cancelled') return false;
      const start = parseISO(assignment.assignment_start);
      const end = parseISO(assignment.assignment_end);
      return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end);
    });
  };

  // Group all assignments (jobs and leads) for a specific day
  const getGroupedAssignments = (day) => {
    const dayJobAssignments = getJobAssignmentsForDay(day);
    const dayLeadAssignments = getLeadAssignmentsForDay(day);
    
    const groups = [];

    // Group job assignments by job
    const jobGroups = {};
    dayJobAssignments.forEach(assignment => {
      const jobId = assignment.job_id;
      if (!jobGroups[jobId]) {
        const job = jobs.find(j => j.id === jobId);
        jobGroups[jobId] = {
          id: jobId,
          code: assignment.job_code || job?.job_code || '',
          title: assignment.job_title || job?.title || 'Unknown Job',
          type: 'job',
          estimators: []
        };
      }
      jobGroups[jobId].estimators.push({
        member_id: assignment.member_id,
        member_name: assignment.member_name,
        planned_hours: assignment.planned_hours_per_day || 8,
        status: assignment.status,
        assignment_type: null
      });
    });
    groups.push(...Object.values(jobGroups));

    // Group lead assignments by lead
    const leadGroups = {};
    dayLeadAssignments.forEach(assignment => {
      const leadId = assignment.lead_id;
      if (!leadGroups[leadId]) {
        leadGroups[leadId] = {
          id: leadId,
          code: assignment.lead_code || '',
          title: assignment.lead_title || 'Unknown Lead',
          type: 'lead',
          estimators: []
        };
      }
      leadGroups[leadId].estimators.push({
        member_id: assignment.member_id,
        member_name: assignment.member_name,
        assignment_type: assignment.assignment_type,
        description: assignment.description,
        status: assignment.status
      });
    });
    groups.push(...Object.values(leadGroups));

    return groups;
  };

  // Get all assignments for statistics
  const getAllAssignmentsForDay = (day) => {
    return [...getJobAssignmentsForDay(day), ...getLeadAssignmentsForDay(day)];
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Estimator Calendar - Week of ${format(currentWeekStart, 'MMM dd, yyyy')}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 24px; color: #1e3a5f; }
            .header p { color: #666; margin-top: 5px; }
            .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
            .day-column { border: 1px solid #e5e7eb; border-radius: 8px; min-height: 200px; }
            .day-header { background: #7c3aed; color: white; padding: 10px; text-align: center; border-radius: 8px 8px 0 0; }
            .day-header .day-name { font-weight: bold; font-size: 14px; }
            .day-header .day-date { font-size: 12px; opacity: 0.9; }
            .day-content { padding: 8px; }
            .job-group { background: #faf5ff; border: 1px solid #e9d5ff; border-radius: 6px; margin-bottom: 8px; padding: 8px; }
            .lead-group { background: #fef3c7; border: 1px solid #fcd34d; border-radius: 6px; margin-bottom: 8px; padding: 8px; }
            .job-header { font-weight: bold; font-size: 11px; color: #6b21a8; margin-bottom: 4px; border-bottom: 1px solid #e9d5ff; padding-bottom: 4px; }
            .lead-header { font-weight: bold; font-size: 11px; color: #b45309; margin-bottom: 4px; border-bottom: 1px solid #fcd34d; padding-bottom: 4px; }
            .job-code { color: #7c3aed; font-family: monospace; }
            .lead-code { color: #d97706; font-family: monospace; }
            .estimator { font-size: 10px; padding: 3px 6px; background: #ede9fe; border-radius: 3px; margin: 2px 0; color: #5b21b6; }
            .lead-estimator { font-size: 10px; padding: 3px 6px; background: #fef9c3; border-radius: 3px; margin: 2px 0; color: #92400e; }
            .no-assignments { color: #9ca3af; font-size: 11px; text-align: center; padding: 20px; }
            .type-badge { font-size: 9px; padding: 1px 4px; border-radius: 3px; margin-left: 4px; }
            .type-job { background: #7c3aed; color: white; }
            .type-lead { background: #d97706; color: white; }
            @media print {
              body { padding: 10px; }
              .day-column { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" alt="MBConstruction" style="height: 80px; margin-bottom: 10px;" />
            <p>Estimator Calendar - Week of ${format(currentWeekStart, 'MMMM dd, yyyy')} to ${format(weekEnd, 'MMMM dd, yyyy')}</p>
          </div>
          <div class="calendar-grid">
            ${weekDays.map(day => {
              const groups = getGroupedAssignments(day);
              return `
                <div class="day-column">
                  <div class="day-header">
                    <div class="day-name">${format(day, 'EEE')}</div>
                    <div class="day-date">${format(day, 'MMM dd')}</div>
                  </div>
                  <div class="day-content">
                    ${groups.length === 0 
                      ? '<div class="no-assignments">No estimates</div>'
                      : groups.map(group => `
                        <div class="${group.type === 'lead' ? 'lead-group' : 'job-group'}">
                          <div class="${group.type === 'lead' ? 'lead-header' : 'job-header'}">
                            ${group.code ? `<span class="${group.type === 'lead' ? 'lead-code' : 'job-code'}">${group.code}</span> - ` : ''}
                            ${group.title}
                            <span class="type-badge type-${group.type}">${group.type.toUpperCase()}</span>
                          </div>
                          ${group.estimators.map(e => `
                            <div class="${group.type === 'lead' ? 'lead-estimator' : 'estimator'}">
                              ${e.member_name}
                              ${e.assignment_type ? ` (${e.assignment_type.replace('_', ' ')})` : ''}
                            </div>
                          `).join('')}
                        </div>
                      `).join('')
                    }
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const goToPreviousWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const goToNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading estimator calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="estimator-calendar" ref={printRef}>
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Week of {format(currentWeekStart, 'MMMM dd')} - {format(weekEnd, 'MMMM dd, yyyy')}
          </h2>
          <p className="text-slate-600 text-sm mt-1">Estimator assignments grouped by job per day</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            data-testid="print-estimator-calendar-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={goToPreviousWeek}
            data-testid="prev-week-btn"
            className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToCurrentWeek}
            className="px-4 py-2 bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium rounded-lg transition-colors"
          >
            This Week
          </button>
          <button
            onClick={goToNextWeek}
            data-testid="next-week-btn"
            className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-sm text-slate-600">Job assignment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-sm text-slate-600">Lead assignment</span>
        </div>
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600">Estimator count</span>
        </div>
        <div className="text-sm text-slate-500">
          {estimators.length} estimator(s) in system
        </div>
      </div>

      {/* Weekly Calendar Grid */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 divide-x divide-slate-200">
          {weekDays.map((day, idx) => {
            const isToday = isSameDay(day, new Date());
            const groups = getGroupedAssignments(day);
            
            return (
              <div 
                key={idx} 
                data-testid={`estimator-day-${format(day, 'yyyy-MM-dd')}`}
                className={`min-h-[400px] flex flex-col ${isToday ? 'bg-purple-50/30' : ''}`}
              >
                {/* Day Header */}
                <div className={`p-3 text-center border-b ${isToday ? 'bg-purple-600 text-white' : 'bg-slate-50'}`}>
                  <div className={`text-sm font-semibold ${isToday ? 'text-white' : 'text-slate-700'}`}>
                    {format(day, 'EEE')}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? 'text-white' : 'text-slate-900'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className={`text-xs ${isToday ? 'text-purple-100' : 'text-slate-500'}`}>
                    {format(day, 'MMM')}
                  </div>
                </div>

                {/* Day Content - Jobs and Leads grouped with estimators */}
                <div className="flex-1 p-2 overflow-y-auto">
                  {groups.length === 0 ? (
                    <div className="text-center text-slate-400 text-sm py-4">
                      No estimates
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {groups.map((group, groupIdx) => (
                        <Link 
                          key={groupIdx} 
                          to={group.type === 'lead' ? `/leads/${group.id}` : `/jobs/${group.id}`}
                          className="block"
                        >
                          <div 
                            data-testid={`estimator-${group.type}-group-${group.id}`}
                            className={`rounded-lg p-2 hover:shadow-sm transition-all cursor-pointer ${
                              group.type === 'lead' 
                                ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 hover:border-amber-400' 
                                : 'bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 hover:border-purple-400'
                            }`}
                          >
                            {/* Header */}
                            <div className={`flex items-start justify-between gap-1 mb-2 pb-1 border-b ${
                              group.type === 'lead' ? 'border-amber-200' : 'border-purple-200'
                            }`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  {group.type === 'lead' && <Target className="w-3 h-3 text-amber-600 flex-shrink-0" />}
                                  {group.code && (
                                    <span className={`text-xs font-mono font-bold truncate ${
                                      group.type === 'lead' ? 'text-amber-600' : 'text-purple-600'
                                    }`}>
                                      {group.code}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs font-medium text-slate-700 block truncate" title={group.title}>
                                  {group.title}
                                </span>
                              </div>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                  group.type === 'lead' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {group.type.toUpperCase()}
                                </span>
                                <span className={`flex items-center gap-1 text-xs bg-white px-1.5 py-0.5 rounded ${
                                  group.type === 'lead' ? 'text-amber-600' : 'text-purple-600'
                                }`}>
                                  <ClipboardList className="w-3 h-3" />
                                  {group.estimators.length}
                                </span>
                              </div>
                            </div>

                            {/* Estimators List */}
                            <div className="space-y-1">
                              {group.estimators.map((estimator, estIdx) => (
                                <div 
                                  key={estIdx}
                                  data-testid={`estimator-${estimator.member_id}`}
                                  className={`flex items-center justify-between bg-white rounded px-2 py-1 text-xs border ${
                                    group.type === 'lead' ? 'border-amber-100' : 'border-purple-100'
                                  }`}
                                >
                                  <span className="font-medium text-slate-700 truncate flex-1" title={estimator.member_name}>
                                    {estimator.member_name}
                                  </span>
                                  {group.type === 'lead' && estimator.assignment_type && (
                                    <span className="text-amber-500 ml-1 whitespace-nowrap text-[10px]">
                                      {estimator.assignment_type.replace('_', ' ')}
                                    </span>
                                  )}
                                  {group.type === 'job' && estimator.planned_hours && (
                                    <span className="text-purple-500 ml-1 whitespace-nowrap">
                                      {estimator.planned_hours}h
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Total Assignments This Week</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">
            {weekDays.reduce((total, day) => total + getAllAssignmentsForDay(day).length, 0)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Jobs Being Estimated</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">
            {new Set(
              weekDays.flatMap(day => 
                getJobAssignmentsForDay(day).map(a => a.job_id)
              )
            ).size}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Leads Being Worked</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">
            {new Set(
              weekDays.flatMap(day => 
                getLeadAssignmentsForDay(day).map(a => a.lead_id)
              )
            ).size}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Active Estimators This Week</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">
            {new Set(
              weekDays.flatMap(day => 
                getAllAssignmentsForDay(day).map(a => a.member_id)
              )
            ).size}
          </div>
        </div>
      </div>

      {/* Estimators List */}
      {estimators.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Registered Estimators</h3>
          <div className="flex flex-wrap gap-2">
            {estimators.map(est => (
              <span 
                key={est.id}
                className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm"
              >
                {est.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimatorCalendar;
